import React from 'react';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  EmojiEvents as WinIcon,
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  Info as InfoIcon,
  LocalOffer as PromoIcon,
  NewReleases as NewIcon,
  Notifications as DefaultIcon,
  Merge as MergeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Notification, WinReason } from '../../api';
import { formatRelativeTime } from '../../utils';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onClick?: () => void;
  dense?: boolean;
}

// Get icon based on notification type or metadata
function getNotificationIcon(notification: Notification) {
  const iconType = notification.metadata?.iconType;

  switch (iconType) {
    case 'win':
      return <WinIcon sx={{ color: '#FFD700' }} />;
    case 'approve':
      return <ApproveIcon sx={{ color: 'success.main' }} />;
    case 'deny':
      return <DenyIcon sx={{ color: 'error.main' }} />;
    case 'promo':
      return <PromoIcon sx={{ color: 'secondary.main' }} />;
    case 'new':
      return <NewIcon sx={{ color: 'info.main' }} />;
    case 'merge':
      return <MergeIcon sx={{ color: 'warning.main' }} />;
    case 'info':
    default:
      // Also check notification type for fallback
      if (notification.type.includes('winner') || notification.type.includes('payout')) {
        return <WinIcon sx={{ color: '#FFD700' }} />;
      }
      if (notification.type.includes('approved')) {
        return <ApproveIcon sx={{ color: 'success.main' }} />;
      }
      if (notification.type.includes('denied')) {
        return <DenyIcon sx={{ color: 'error.main' }} />;
      }
      if (notification.type.includes('system')) {
        return <InfoIcon sx={{ color: 'info.main' }} />;
      }
      if (notification.type.includes('merge')) {
        return <MergeIcon sx={{ color: 'warning.main' }} />;
      }
      return <DefaultIcon color="action" />;
  }
}

// Get readable label for win reason type
function getWinReasonLabel(type: WinReason['type']): string {
  switch (type) {
    case 'main':
      return 'Main';
    case 'wrong_way':
      return 'Wrong Way';
    case 'plus_n':
      return '+N';
    case 'plus_n_wrong_way':
      return '+N WW';
    case 'neighbor':
      return 'Neighbor';
    case 'box_neighbor':
      return 'Box';
    default:
      return type;
  }
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onClick,
  dense = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    // Mark as read if not already
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate if there's an action URL
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }

    // Call additional onClick handler
    onClick?.();
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    onClick?.();
  };

  return (
    <ListItem
      disablePadding
      sx={{
        backgroundColor: notification.isRead
          ? 'transparent'
          : alpha(theme.palette.primary.main, 0.04),
        borderLeft: notification.isRead ? 'none' : `3px solid ${theme.palette.primary.main}`,
      }}
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: dense ? 1 : 1.5,
          px: dense ? 1.5 : 2,
          alignItems: 'flex-start',
        }}
      >
        <ListItemIcon sx={{ minWidth: dense ? 36 : 40, mt: 0.5 }}>
          {getNotificationIcon(notification)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Typography
                variant={dense ? 'body2' : 'subtitle2'}
                sx={{
                  fontWeight: notification.isRead ? 400 : 600,
                  flex: 1,
                  pr: 1,
                  // Truncate on mobile
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {notification.title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {formatRelativeTime(notification.createdAt)}
              </Typography>
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  // Truncate message
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {notification.message}
              </Typography>
              {/* Show win reasons breakdown if available */}
              {notification.metadata?.winReasons && notification.metadata.winReasons.length > 1 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {notification.metadata.winReasons.map((reason, idx) => (
                    <Chip
                      key={idx}
                      label={`${getWinReasonLabel(reason.type)}: $${reason.amount.toFixed(2)}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: reason.type === 'main' ? 'success.light' : 'grey.200',
                        color: reason.type === 'main' ? 'success.contrastText' : 'text.secondary',
                      }}
                    />
                  ))}
                </Box>
              )}
              {notification.actionLabel && notification.actionUrl && (
                <Button
                  size="small"
                  onClick={handleActionClick}
                  sx={{ mt: 0.5, p: 0, minWidth: 'auto', textTransform: 'none' }}
                >
                  {notification.actionLabel}
                </Button>
              )}
              {notification.metadata?.amount !== undefined && (
                <Typography
                  variant="body2"
                  sx={{
                    color: notification.metadata.amount >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  {notification.metadata.amount >= 0
                    ? `+$${notification.metadata.amount.toFixed(2)}`
                    : `You owe $${Math.abs(notification.metadata.amount).toFixed(2)}`}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};
